import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import AppModal from "../common/AppModal";
import FormDateField from "../common/FormDateField";
import FormAlert from "../common/FormAlert";
import AppButton from "../buttons/AppButton";

import { getCompanyCharges } from "../../redux/actions/companyChargeActions";
import {
  updateQuotation,
  resetQuoteServerStatus,
} from "../../redux/actions/quotationActions";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { QuotationResponseDto } from "../../shared/dtos/quotation/QuotationResponseDto";
import { QuotationUpdateDto } from "../../shared/dtos/quotation/QuotationUpdateDto";
import { formatIsoDateForInput } from "../../utils/appUtils";

interface UpdateQuotationModalProps {
  open: boolean;
  onClose: () => void;
  quote: QuotationResponseDto;
}

const UpdateQuotationModal: React.FC<UpdateQuotationModalProps> = ({
  open,
  onClose,
  quote,
}) => {
  const dispatch = useAppDispatch();

  const {
    loadingUpdate: loadingUpdateQuotation,
    serverStatus,
    errorUpdate: errorUpdateQuote,
  } = useAppSelector((state) => state.quotation);
  const { items: companyCharges } = useAppSelector(
    (state) => state.companyCharge,
  );

  useEffect(() => {
    dispatch(getCompanyCharges());
  }, [dispatch]);

  const [expiryDate, setExpiryDate] = useState<string>(
    formatIsoDateForInput(quote.expiryDate),
  );
  const [termsAndConditions, setTermsAndConditions] = useState<string>(
    quote.termsAndConditions || "",
  );

  const [chargeIds, setChargeIds] = useState<string[]>(
    (quote.charges || []).map((c) => c.id || c.id).filter(Boolean),
  );

  const [items, setItems] = useState(
    quote.items.map((i) => ({
      itemId: i.productId || i.id,
      name: i.name,
      qty: i.qty,
      price: i.price,
      discount: i.discount || 0,
    })),
  );

  useEffect(() => {
    if (serverStatus?.operation === "update" && serverStatus?.status === 200) {
      dispatch(resetQuoteServerStatus());
      onClose();
    }
  }, [serverStatus, dispatch, onClose]);

  const handleUpdate = (): void => {
    const payload: QuotationUpdateDto = {
      expiryDate: new Date(expiryDate).toISOString().split(".")[0] + "Z",
      termsAndConditions,
      items: items.map((i) => ({
        itemId: i.itemId,
        qty: Number(i.qty),
        price: Number(i.price),
        discount: Number(i.discount),
      })),
      chargeIds: chargeIds.filter(Boolean),
    };

    dispatch(updateQuotation(quote.id, payload));
  };

  const handleItemChange = (
    idx: number,
    field: string,
    value: string | number,
  ): void => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    setItems(newItems);
  };

  const handleRemoveItem = (idx: number): void => {
    const newItems = items.filter((_, index) => index !== idx);
    setItems(newItems);
  };

  const dynamicSubtotal = items.reduce(
    (acc, item) =>
      acc + (Number(item.price) - Number(item.discount)) * Number(item.qty),
    0,
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Update Quotation #${quote.quoteNumber}`}
      width="lg"
      loading={loadingUpdateQuotation}
    >
      <Box sx={{ width: "100%" }}>
        <FormAlert
          message={errorUpdateQuote}
          severity="error"
          closable
          onClose={() => dispatch(resetQuoteServerStatus())}
        />
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <FormDateField
              label="Valid Until (Expiry Date)"
              value={expiryDate}
              onChange={(value) => setExpiryDate(value)}
              required
            />
            <FormControl fullWidth color="secondary">
              <InputLabel>Update Charges</InputLabel>
              <Select
                multiple
                value={chargeIds}
                onChange={(e) => setChargeIds(e.target.value as string[])}
                input={<OutlinedInput label="Update Charges" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((val) => {
                      // Fetch name from CompanyCharges lookup OR original Quote Charges fallback
                      const charge =
                        companyCharges?.find((c) => c.id === val) ||
                        quote.charges?.find((c) => c.id === val);

                      return (
                        <Chip
                          key={val}
                          label={`${charge?.name || "Loading..."} (${charge?.rate || 0}%)`}
                          size="small"
                          onDelete={() =>
                            setChargeIds((prev) =>
                              prev.filter((id) => id !== val),
                            )
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {companyCharges?.map((charge) => (
                  <MenuItem key={charge.id} value={charge.id}>
                    {charge.name} ({charge.rate}%) - {charge.chargeType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Terms & Conditions"
            multiline
            rows={3}
            value={termsAndConditions}
            onChange={(e) => setTermsAndConditions(e.target.value)}
            color="secondary"
            fullWidth
          />

          <Box
            sx={{
              maxHeight: 350,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Discount/Unit</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => {
                  const lineTotal =
                    (Number(item.price) - Number(item.discount)) *
                    Number(item.qty);
                  return (
                    <TableRow key={item.itemId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          color="secondary"
                          value={item.qty}
                          onChange={(e) =>
                            handleItemChange(idx, "qty", e.target.value)
                          }
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          color="secondary"
                          value={item.price}
                          onChange={(e) =>
                            handleItemChange(idx, "price", e.target.value)
                          }
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          color="secondary"
                          value={item.discount}
                          onChange={(e) =>
                            handleItemChange(idx, "discount", e.target.value)
                          }
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {Number(lineTotal).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
              <Typography variant="h6">Preview Subtotal: </Typography>
              <Typography sx={{ color: "secondary.main", fontWeight: "bold" }}>
                {quote.currencyCode}{" "}
                {dynamicSubtotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Stack>

            <AppButton
              pill
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              disabled={items.length === 0 || loadingUpdateQuotation}
              isLoading={loadingUpdateQuotation}
            >
              Save Changes
            </AppButton>
          </Stack>
        </Stack>
      </Box>
    </AppModal>
  );
};

export default UpdateQuotationModal;
